import { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "./useDebouncedValue.js";

const SPELLER_URL = "https://speller.yandex.net/services/spellservice.json/checkText";

// Опции Яндекс.Спеллера (битовая маска): 1 — игнор цифр, 2 — игнор URL,
// 4 — определяет повтор слов, 8 — игнор заглавных. Описание: yandex.ru/dev/speller
const DEFAULT_OPTIONS = 2 + 8;

const cache = new Map();
const MAX_CACHE = 200;

function cacheKey(text, lang, options) {
  return `${lang}|${options}|${text}`;
}

function setCache(key, value) {
  if (cache.size >= MAX_CACHE) {
    const firstKey = cache.keys().next().value;
    if (firstKey) cache.delete(firstKey);
  }
  cache.set(key, value);
}

/**
 * Проверка орфографии через Яндекс.Спеллер.
 *
 * @param {string} text   проверяемый текст
 * @param {object} opts
 * @param {string} opts.lang     "ru", "en" или "ru,en" (по умолчанию)
 * @param {boolean} opts.enabled выкл/вкл
 * @param {number} opts.delayMs  debounce, мс
 * @param {number} opts.options  битовая маска опций спеллера
 * @returns {{pos:number,len:number,word:string,suggestions:string[]}[]}
 */
export function useSpellCheck(text, {
  lang = "ru,en",
  enabled = true,
  delayMs = 500,
  options = DEFAULT_OPTIONS,
} = {}) {
  const [issues, setIssues] = useState([]);
  const debounced = useDebouncedValue(text, delayMs);
  const lastReqIdRef = useRef(0);

  const trimmedKey = useMemo(
    () => (typeof debounced === "string" ? debounced : ""),
    [debounced],
  );

  useEffect(() => {
    if (!enabled) {
      setIssues([]);
      return;
    }
    if (!trimmedKey || !trimmedKey.trim()) {
      setIssues([]);
      return;
    }

    const key = cacheKey(trimmedKey, lang, options);
    const cached = cache.get(key);
    if (cached) {
      setIssues(cached);
      return;
    }

    const reqId = ++lastReqIdRef.current;
    const ctrl = new AbortController();
    const params = new URLSearchParams({
      lang,
      options: String(options),
      format: "plain",
      text: trimmedKey,
    });

    fetch(`${SPELLER_URL}?${params.toString()}`, {
      method: "GET",
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (reqId !== lastReqIdRef.current) return;
        const mapped = Array.isArray(data)
          ? data
              .filter((it) => it && typeof it.pos === "number" && typeof it.len === "number")
              .map((it) => ({
                pos: it.pos,
                len: it.len,
                word: it.word || "",
                suggestions: Array.isArray(it.s) ? it.s.filter(Boolean) : [],
              }))
          : [];
        setCache(key, mapped);
        setIssues(mapped);
      })
      .catch(() => {
        if (reqId !== lastReqIdRef.current) return;
      });

    return () => ctrl.abort();
  }, [trimmedKey, lang, enabled, options]);

  return issues;
}
